import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Grid, 
  Card, 
  CardContent, 
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
  Paper,
  Button,
  Container,
  Alert
} from '@mui/material';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Opportunity, LocationType } from '../../types/models';
import { ROUTES } from '../../config/routes';
import { CITIES, INDUSTRIES, LOCATION_TYPES, type Industry, type City } from '../../constants/options';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
// Constants for filter options
const SORT_OPTIONS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'hours-asc', label: 'Hours: Low to High' },
  { value: 'hours-desc', label: 'Hours: High to Low' },
  { value: 'spots-desc', label: 'Most Spots Available' }
];

const FILTER_STORAGE_KEY = 'opportunity_filters';

interface FilterState {
  searchTerm: string;
  sortBy: string;
  selectedIndustry: Industry | '';
  selectedCity: City | '';
  selectedLocationType: LocationType | '';
}

const saveFilters = (filters: FilterState) => {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
};

const loadFilters = (): FilterState => {
  const saved = localStorage.getItem(FILTER_STORAGE_KEY);
  if (!saved) return {
    searchTerm: '',
    sortBy: 'recent',
    selectedIndustry: '',
    selectedCity: '',
    selectedLocationType: ''
  };
  return JSON.parse(saved);
};

const OpportunityListPage: React.FC = () => {
  // Load saved filters on initial render
  const savedFilters = React.useMemo(() => loadFilters(), []);
  
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states with saved values
  const [sortBy, setSortBy] = useState(savedFilters.sortBy);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | ''>(savedFilters.selectedIndustry);
  const [selectedCity, setSelectedCity] = useState<City | ''>(savedFilters.selectedCity);
  const [selectedLocationType, setSelectedLocationType] = useState<LocationType | ''>(savedFilters.selectedLocationType);

  // Add effect to save filters when they change
  useEffect(() => {
    const currentFilters: FilterState = {
      searchTerm,
      sortBy,
      selectedIndustry,
      selectedCity,
      selectedLocationType
    };
    saveFilters(currentFilters);
  }, [searchTerm, sortBy, selectedIndustry, selectedCity, selectedLocationType]);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching opportunities...');
      const response = await api.opportunities.getOpportunities();
      console.log('API Response:', response);
      
      if (!response || !Array.isArray(response)) {
        throw new Error('Invalid response format from API');
      }
      
      console.log('Processed opportunities:', response.length, 'items');
      
      if (response.length === 0) {
        console.log('No opportunities found in response');
      } else {
        console.log('First opportunity:', response[0]);
      }
      
      setOpportunities(response);
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError('Failed to load opportunities. Please try again later.');
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedOpportunities = React.useMemo(() => {
    if (!Array.isArray(opportunities)) return [];
    
    let result = [...opportunities];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(opp => 
        opp.title.toLowerCase().includes(searchLower) ||
        opp.description.toLowerCase().includes(searchLower) ||
        opp.organization.name.toLowerCase().includes(searchLower) ||
        opp.organization.industry.toLowerCase().includes(searchLower)
      );
    }

    // Apply industry filter
    if (selectedIndustry) {
      result = result.filter(opp => opp.organization.industry === selectedIndustry);
    }

    // Apply location type filter
    if (selectedLocationType) {
      result = result.filter(opp => 
        opp.location_type.toLowerCase() === selectedLocationType.toLowerCase()
      );
    }

    // Apply city filter
    if (selectedCity) {
      result = result.filter(opp => 
        opp.organization.city === selectedCity
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'hours-asc':
        result.sort((a, b) => a.commitment_hours - b.commitment_hours);
        break;
      case 'hours-desc':
        result.sort((a, b) => b.commitment_hours - a.commitment_hours);
        break;
      case 'spots-desc':
        result.sort((a, b) => b.available_spots - a.available_spots);
        break;
      case 'recent':
      default:
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return result;
  }, [opportunities, searchTerm, selectedIndustry, selectedLocationType, selectedCity, sortBy]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedIndustry('');
    setSelectedCity('');
    setSelectedLocationType('');
    setSortBy('recent');
    // Clear saved filters
    localStorage.removeItem(FILTER_STORAGE_KEY);
  };

  const renderOpportunityCard = (opportunity: Opportunity) => (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        }
      }}
    >
        <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 'bold'
            }}
          >
            {opportunity.title}
          </Typography>
        </Box>
          
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {opportunity.organization.name}
                {opportunity.organization.industry && ` • ${opportunity.organization.industry}`}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {opportunity.commitment_hours} hours
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Required Skills
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {opportunity.required_skills.map(skill => (
                <Chip
                  key={skill}
                  label={skill}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
        <Box sx={{ p: 2, pt: 2 }}>
        <Button
          component={Link}
          to={ROUTES.STUDENT.OPPORTUNITIES.DETAIL(opportunity.id)}
          variant="contained"
          fullWidth
          endIcon={<ArrowForwardIcon />}
        >
          View Details
        </Button>
      </Box>
    </Card>
  );
  
  // Render loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search Opportunities"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={<SortIcon sx={{ mr: 1 }} />}
              >
                {SORT_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={selectedIndustry}
                label="Industry"
                onChange={(e) => setSelectedIndustry(e.target.value as Industry)}
                startAdornment={<BusinessIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="">All Industries</MenuItem>
                {INDUSTRIES.map(industry => (
                  <MenuItem key={industry.value} value={industry.value}>
                    {industry.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Location Type</InputLabel>
              <Select
                value={selectedLocationType}
                label="Location Type"
                onChange={(e) => setSelectedLocationType(e.target.value as LocationType)}
                startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="">All Types</MenuItem>
                {LOCATION_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select
                value={selectedCity}
                label="City"
                onChange={(e) => setSelectedCity(e.target.value as City)}
                startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="">All Cities</MenuItem>
                {CITIES.map(city => (
                  <MenuItem key={city.value} value={city.value}>
                    {city.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {(searchTerm || selectedIndustry || selectedCity || selectedLocationType || sortBy !== 'recent') && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                startIcon={<FilterListIcon />}
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Results Section */}
      {filteredAndSortedOpportunities.length === 0 ? (
        <Alert severity="info">
          No opportunities found matching your criteria. Try adjusting your filters.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredAndSortedOpportunities.map((opportunity) => (
            <Grid item xs={12} md={6} lg={4} key={opportunity.id}>
              {renderOpportunityCard(opportunity)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default OpportunityListPage; 